<template>
  <ion-header
    class="page-header liga-float-header ion-no-border"
    :class="{ 'page-header--scrolled': scrolled, 'page-header--no-menu': !showMenuButton }"
  >
    <ion-toolbar class="page-header-toolbar">
      <div class="liga-float-bar">
        <button
          v-if="showMenuButton"
          type="button"
          class="liga-float-circle menu-btn"
          aria-label="Меню"
          @click="openMenu"
        >
          <ion-icon :icon="menuOutline" />
        </button>
        <div v-else class="liga-float-spacer" aria-hidden="true" />

        <div class="liga-float-title-pill">
          <span class="liga-float-title-text">{{ title }}</span>
        </div>

        <button
          v-if="showMessenger"
          type="button"
          class="liga-float-circle messenger-btn"
          aria-label="Liga.Chat"
          @click="openMessenger"
        >
          <span class="ios-unread-icon-wrap">
            <ion-icon :icon="chatbubbleEllipsesOutline" />
            <span v-if="unreadLabel" class="ios-unread-badge ios-unread-badge--on-icon">{{ unreadLabel }}</span>
          </span>
        </button>
        <button
          v-else
          type="button"
          class="liga-float-circle settings-btn"
          aria-label="Настройки"
          @click="openSettings"
        >
          <ion-icon :icon="settingsOutline" />
        </button>
      </div>
    </ion-toolbar>
  </ion-header>
</template>

<script lang="ts">
import {
  IonHeader,
  IonIcon,
  IonToolbar,
} from '@ionic/vue';
import { chatbubbleEllipsesOutline, menuOutline, settingsOutline } from 'ionicons/icons';
import { computed, defineComponent, inject, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
// @ts-ignore
import { MittEmitter } from 'mitt-emitter';
import { GlobalContext } from '@/model/globalcontext.model';
import {
  formatMessengerUnread,
  getMessengerUnreadTotal,
  refreshMessengerUnread,
  subscribeMessengerUnread,
} from '@/services/messenger-unread';
import store from '@/store';

export default defineComponent({
  name: 'PageHeaderWithMenu',
  components: {
    IonHeader,
    IonIcon,
    IonToolbar,
  },
  props: {
    title: { type: String, required: true },
    scrolled: { type: Boolean, default: false },
  },
  setup() {
    const emitter = inject('emitter') as MittEmitter | undefined;
    const globalContext = inject('globalContext') as GlobalContext;
    const router = useRouter();
    const unreadCount = ref(getMessengerUnreadTotal());
    const desktopLayout = ref(false);
    const desktopMenuCollapsed = ref(false);
    let unsubscribeUnread: (() => void) | undefined;
    let mediaQuery: MediaQueryList | undefined;
    let onMediaChange: (() => void) | undefined;
    let onDesktopMenuCollapsed: ((collapsed: boolean) => void) | undefined;

    const unreadLabel = computed(() => formatMessengerUnread(unreadCount.value));
    /** На desktop сайдбар; бургер нужен на мобиле или когда сайдбар свёрнут. */
    const showMenuButton = computed(
      () => globalContext.isMobile || !desktopLayout.value || desktopMenuCollapsed.value
    );
    const showMessenger = computed(() => !!store.getters.authenticated);

    onMounted(() => {
      unsubscribeUnread = subscribeMessengerUnread(count => {
        unreadCount.value = count;
      });
      if (store.getters.authenticated) {
        void refreshMessengerUnread();
      }

      if (typeof window !== 'undefined') {
        try {
          desktopMenuCollapsed.value = localStorage.getItem('liga-desktop-menu-collapsed') === '1';
        } catch {
          desktopMenuCollapsed.value = false;
        }
        mediaQuery = window.matchMedia('(min-width: 992px)');
        onMediaChange = () => {
          desktopLayout.value = mediaQuery?.matches ?? false;
        };
        onMediaChange();
        mediaQuery.addEventListener('change', onMediaChange);
      }

      onDesktopMenuCollapsed = (collapsed: boolean) => {
        desktopMenuCollapsed.value = !!collapsed;
      };
      emitter?.on('desktop-menu-collapsed', onDesktopMenuCollapsed);
    });

    onUnmounted(() => {
      unsubscribeUnread?.();
      if (mediaQuery && onMediaChange) {
        mediaQuery.removeEventListener('change', onMediaChange);
      }
      if (onDesktopMenuCollapsed) {
        emitter?.off('desktop-menu-collapsed', onDesktopMenuCollapsed);
      }
    });

    const openMenu = () => {
      emitter?.emit('open-menu');
    };
    const openMessenger = () => router.push({ name: 'MessengerInbox' });
    const openSettings = () => router.push({ name: 'Settings' });
    return {
      chatbubbleEllipsesOutline,
      menuOutline,
      settingsOutline,
      openMenu,
      openMessenger,
      openSettings,
      unreadLabel,
      showMenuButton,
      showMessenger,
    };
  },
});
</script>

<style scoped>
.page-header {
  --background: transparent;
  --border-width: 0;
  --min-height: 56px;
  background: transparent;
  box-shadow: none;
}

.page-header-toolbar {
  --background: transparent;
  /* fallback 20px: на native inset иногда 0 до StatusBar overlay */
  --padding-top: max(env(safe-area-inset-top, 0px), var(--ion-safe-area-top, 0px), 20px);
  --padding-start: 0;
  --padding-end: 0;
  --min-height: 56px;
  --border-width: 0;
}

.liga-float-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 56px;
  padding: 6px 10px 8px;
  box-sizing: border-box;
}

.liga-float-spacer {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}

.menu-btn,
.messenger-btn,
.settings-btn {
  appearance: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--liga-chrome-text);
}

.menu-btn ion-icon,
.messenger-btn ion-icon,
.settings-btn ion-icon {
  font-size: 22px;
  color: var(--liga-chrome-text);
}

.liga-float-title-text {
  font-size: 0.95rem;
  font-weight: 650;
  letter-spacing: 0.01em;
  color: var(--liga-chrome-text);
}
</style>
